// ---------------------------------------------------------------------------
// YouAreYou (uru) — ConnectionsPage (Your Trusted Network)  — 2026 rose refresh
//
// Matches screen-mockups/redesign/contacts_directory_youareyou:
//   - "Your Trusted Network" hero
//   - Search + All / Verified / Pending segmented filter
//   - Monolithic white card with a table: NAME · TRUST · STATUS · VERIFIED
//     FACTORS · LAST ACTIVITY · (open)
//   - "Showing N connections" footer
//   - Two CTA cards: Add a connection · Activity ledger
//   - Mobile: stacked rows + floating "New Trust Link"
//
// All data is real (GET /api/uru/connections + detail). Statuses, factors, and
// names come from connected_customer flags — nothing invented.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { listConnections, getConnection, grantDataSharing } from "@/api/endpoints";
import { ApiRequestError, NetworkError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import ContactDetailModal from "@/components/ui/ContactDetailModal";
import NewTrustLinkModal from "@/components/ui/NewTrustLinkModal";
import type { Connection, ConnectionStatus, SharableField } from "@/types/api";
import { useContactMatch } from "@/hooks/useContactMatch";

const CARD = "rounded-2xl bg-white shadow-[0px_4px_20px_rgba(26,31,43,0.04)]";

// ---------------------------------------------------------------------------
// Icon helper
// ---------------------------------------------------------------------------

function Icon({
  name,
  filled = false,
  className = "",
}: {
  name: string;
  filled?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

function relativeActivity(unix: number): string {
  const diff = Date.now() / 1000 - unix;
  if (diff < 3600) {
    const m = Math.max(1, Math.floor(diff / 60));
    return `${m} min${m !== 1 ? "s" : ""} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h !== 1 ? "s" : ""} ago`;
  }
  if (diff < 172800) return "Yesterday";
  if (diff < 7 * 86400) {
    const d = Math.floor(diff / 86400);
    return `${d} days ago`;
  }
  return new Date(unix * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VerificationFactor {
  label: string;
  icon: string;
}

interface StatusDisplay {
  label: string;
  cls: string;
}

interface EnrichedConnection extends Connection {
  displayName: string;
  initials: string;
  sortFirstName: string;
  factors: VerificationFactor[];
  isInbound: boolean;
  lastActivity: string;
  statusDisplay: StatusDisplay;
  isTrusted: boolean;
  avatarUrl: string | null;
  phone: string | null;
}

// ---------------------------------------------------------------------------
// Verified Factors derivation
// ---------------------------------------------------------------------------

function deriveFactors(conn: Connection): VerificationFactor[] {
  const person = conn.connected_customer;
  if (!person) return [];

  const factors: VerificationFactor[] = [];
  if (person.is_gov_id_verified) factors.push({ label: "Gov ID", icon: "badge" });
  if (person.is_liveness_verified) factors.push({ label: "Liveness", icon: "face" });
  if (person.is_phone_verified) factors.push({ label: "Phone", icon: "smartphone" });
  if (person.is_email_verified) factors.push({ label: "Email", icon: "mail" });
  if (person.is_ssn_verified) factors.push({ label: "SSN", icon: "fingerprint" });
  if (person.is_ofac_verified) factors.push({ label: "OFAC", icon: "gpp_good" });
  if (person.is_address_verified) factors.push({ label: "Address", icon: "home" });
  return factors;
}

// ---------------------------------------------------------------------------
// Status display (rose palette)
// ---------------------------------------------------------------------------

function getStatusDisplay(status: ConnectionStatus, isInbound: boolean): StatusDisplay {
  switch (status) {
    case "verified":
      return { label: "Completed", cls: "bg-[#e8f3ec] text-[#3f7d54]" };
    case "pending":
      return isInbound
        ? { label: "Action Required", cls: "bg-[#f08b8b] text-white" }
        : { label: "Pending", cls: "bg-[#dbe7ff] text-[#2d5bbf]" };
    case "denied":
      return { label: "Declined", cls: "bg-[#ffe6e3] text-[#ba1a1a]" };
    case "cancelled":
      return { label: "Cancelled", cls: "bg-[#f1f3ff] text-[#877271]" };
    default:
      return { label: status, cls: "bg-[#f1f3ff] text-[#877271]" };
  }
}

// ---------------------------------------------------------------------------
// Deduplicate connections by person
// ---------------------------------------------------------------------------

function deduplicateByName(enriched: EnrichedConnection[]): EnrichedConnection[] {
  const groups = new Map<string, EnrichedConnection[]>();

  for (const conn of enriched) {
    const key = conn.connected_customer
      ? `customer_${conn.connected_customer.id}`
      : `__unclaimed__${conn.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(conn);
  }

  const result: EnrichedConnection[] = [];

  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    const sorted = [...group].sort((a, b) => (b.modified ?? 0) - (a.modified ?? 0));
    const primary = sorted[0];

    const seenLabels = new Set(primary.factors.map((f) => f.label));
    const mergedFactors = [...primary.factors];
    for (const conn of sorted.slice(1)) {
      for (const f of conn.factors) {
        if (!seenLabels.has(f.label)) {
          seenLabels.add(f.label);
          mergedFactors.push(f);
        }
      }
    }

    const isTrusted = sorted.some((c) => c.isTrusted);
    result.push({ ...primary, factors: mergedFactors, isTrusted });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Enrich connections
// ---------------------------------------------------------------------------

function enrichConnection(conn: Connection): EnrichedConnection {
  const isInbound = conn.direction === "incoming";
  const other = conn.connected_customer;

  const firstName = other?.first_name || "";
  const lastName = other?.last_name || "";
  const displayName = other ? `${firstName} ${lastName}`.trim() : "Awaiting Recipient";
  const initials = other ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() : "?";

  return {
    ...conn,
    displayName,
    initials,
    sortFirstName: firstName || "~",
    factors: deriveFactors(conn),
    isInbound,
    lastActivity: relativeActivity(conn.modified ?? conn.created),
    statusDisplay: getStatusDisplay(conn.status, isInbound),
    isTrusted: conn.status === "verified",
    avatarUrl: other?.avatar || null,
    phone: other?.phone || null,
  };
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  "bg-[#fef0f0] text-[#974446]",
  "bg-[#e9edfe] text-[#2d5bbf]",
  "bg-[#e8f3ec] text-[#3f7d54]",
  "bg-[#fff4e0] text-[#92671e]",
  "bg-[#f1ecfb] text-[#6b46a8]",
];

function AvatarWithBadge({
  initials,
  displayName,
  isVerified,
  avatarUrl,
}: {
  initials: string;
  displayName: string;
  isVerified: boolean;
  avatarUrl?: string | null;
}) {
  const colorIndex = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % AVATAR_COLORS.length;

  return (
    <div className="relative shrink-0">
      {avatarUrl ? (
        <img src={avatarUrl} alt={displayName} className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold ${AVATAR_COLORS[colorIndex]}`}
          aria-hidden="true"
        >
          {initials}
        </div>
      )}
      {isVerified && (
        <div
          className="absolute -bottom-0.5 -left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#3f7d54] ring-2 ring-white"
          aria-label={`${displayName} is verified`}
        >
          <Icon name="check" filled className="text-[10px] text-white" />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ display }: { display: StatusDisplay }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${display.cls}`}>
      {display.label}
    </span>
  );
}

function FactorIcons({ factors }: { factors: VerificationFactor[] }) {
  if (factors.length === 0) return <span className="text-xs text-[#b9a9a8]">—</span>;
  return (
    <div className="flex items-center gap-1">
      {factors.slice(0, 4).map((f, i) => (
        <span
          key={i}
          title={f.label}
          className="flex h-6 w-6 items-center justify-center rounded-full bg-[#fef0f0] text-[#974446]"
        >
          <Icon name={f.icon} className="text-[14px]" />
        </span>
      ))}
      {factors.length > 4 && <span className="text-[11px] font-semibold text-[#877271]">+{factors.length - 4}</span>}
    </div>
  );
}

function TrustLabel({ isTrusted }: { isTrusted: boolean }) {
  return isTrusted ? (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#3f7d54]">
      <Icon name="verified_user" filled className="text-sm" />
      Trusted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#b46a2e]">
      <Icon name="schedule" filled className="text-sm" />
      Not yet
    </span>
  );
}

// ---------------------------------------------------------------------------
// ContactRow
// ---------------------------------------------------------------------------

function ContactRow({
  connection,
  onClick,
  hasActiveSharing,
  isSaved,
}: {
  connection: EnrichedConnection;
  onClick: (connection: EnrichedConnection) => void;
  hasActiveSharing?: boolean;
  isSaved?: boolean;
}) {
  const savedChip = isSaved ? (
    <span title="In your contacts" className="inline-flex items-center gap-0.5 rounded-full bg-[#f1f3ff] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#877271]">
      <Icon name="contacts" className="text-[10px]" />
      Saved
    </span>
  ) : null;
  return (
    <button
      onClick={() => onClick(connection)}
      className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[#faf9ff] focus:outline-none"
      aria-label={`View ${connection.displayName}'s profile`}
    >
      {/* Mobile: stacked */}
      <div className="flex w-full items-start gap-3 lg:hidden">
        <AvatarWithBadge initials={connection.initials} displayName={connection.displayName} isVerified={connection.isTrusted} avatarUrl={connection.avatarUrl} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-bold text-[#161b27]">{connection.displayName}</p>
            {hasActiveSharing && <Icon name="sync_alt" className="shrink-0 text-sm text-[#f08b8b]" />}
          </div>
          {connection.phone && (
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-[#877271]">{connection.phone}</p>
              {savedChip}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge display={connection.statusDisplay} />
            <span className="text-xs text-[#877271]">{connection.lastActivity}</span>
          </div>
          <FactorIcons factors={connection.factors} />
        </div>
        <Icon name="chevron_right" className="mt-1 shrink-0 text-xl text-[#877271]" />
      </div>

      {/* Desktop: columns */}
      <div className="hidden w-full items-center gap-3 lg:flex">
        <div className="flex w-[26%] min-w-0 items-center gap-3">
          <AvatarWithBadge initials={connection.initials} displayName={connection.displayName} isVerified={connection.isTrusted} avatarUrl={connection.avatarUrl} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-bold text-[#161b27]">{connection.displayName}</p>
              {hasActiveSharing && <Icon name="sync_alt" className="shrink-0 text-sm text-[#f08b8b]" />}
            </div>
            {connection.phone && (
              <div className="flex items-center gap-1.5">
                <p className="truncate text-xs text-[#877271]">{connection.phone}</p>
                {savedChip}
              </div>
            )}
          </div>
        </div>
        <div className="w-[14%]"><TrustLabel isTrusted={connection.isTrusted} /></div>
        <div className="w-[16%]"><StatusBadge display={connection.statusDisplay} /></div>
        <div className="w-[20%]"><FactorIcons factors={connection.factors} /></div>
        <div className="w-[16%]"><span className="text-xs text-[#877271]">{connection.lastActivity}</span></div>
        <div className="flex flex-1 items-center justify-end">
          <Icon name="more_vert" className="shrink-0 text-xl text-[#877271]" />
        </div>
      </div>
    </button>
  );
}

function TableHeader() {
  const cols = ["Name", "Trust", "Status", "Verified Factors", "Last Activity", ""];
  const widths = ["w-[26%]", "w-[14%]", "w-[16%]", "w-[20%]", "w-[16%]", "flex-1"];
  return (
    <div className="hidden items-center gap-3 border-b border-[#f1f3ff] px-5 py-3 lg:flex">
      {cols.map((c, i) => (
        <span key={i} className={`${widths[i]} text-[11px] font-bold uppercase tracking-wide text-[#877271]`}>
          {c}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search + filter
// ---------------------------------------------------------------------------

type StatusTab = "all" | "verified" | "pending";

function Controls({
  search,
  onSearch,
  tab,
  onTab,
}: {
  search: string;
  onSearch: (v: string) => void;
  tab: StatusTab;
  onTab: (t: StatusTab) => void;
}) {
  const tabs: { key: StatusTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "verified", label: "Verified" },
    { key: "pending", label: "Pending" },
  ];
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative max-w-sm flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon name="search" className="text-lg text-[#877271]" />
        </div>
        <input
          type="search"
          placeholder="Search connections…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="w-full rounded-xl bg-white py-2.5 pl-10 pr-4 text-sm text-[#161b27] placeholder:text-[#b9a9a8] shadow-[0px_4px_20px_rgba(26,31,43,0.04)] focus:outline-none focus:ring-4 focus:ring-[#f08b8b]/15"
          aria-label="Search connections"
        />
      </div>
      <div className="flex shrink-0 rounded-xl bg-white p-1 shadow-[0px_4px_20px_rgba(26,31,43,0.04)]">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onTab(t.key)}
            className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-[#fef0f0] text-[#974446]" : "text-[#877271] hover:text-[#161b27]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function EmptyState({ tab, hasSearch }: { tab: StatusTab; hasSearch: boolean }) {
  if (tab === "pending") {
    return (
      <div className="px-6 py-14 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#fef0f0]">
          <Icon name="inbox" className="text-2xl text-[#974446]" />
        </div>
        <p className="text-sm text-[#544242]">
          Pending requests live in{" "}
          <Link to="/requests" className="font-semibold text-[#974446] hover:text-[#792e30]">Requests →</Link>
        </p>
      </div>
    );
  }
  return (
    <div className="px-6 py-14 text-center">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#f1f3ff]">
        <Icon name="people" className="text-2xl text-[#877271]" />
      </div>
      <h3 className="text-base font-bold text-[#161b27]">{hasSearch ? "No connections found" : "No connections yet"}</h3>
      <p className="mx-auto mt-1 max-w-xs text-sm text-[#544242]">
        {hasSearch ? "Try a different search term." : "Share your trust link to start building your verified network."}
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="px-5 py-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-3.5">
          <div className="h-10 w-10 animate-pulse rounded-full bg-[#f1f3ff]" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-40 animate-pulse rounded bg-[#f1f3ff]" />
            <div className="h-3 w-28 animate-pulse rounded bg-[#f1f3ff]" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="px-6 py-14 text-center">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#ffe6e3]">
        <Icon name="error" className="text-2xl text-[#ba1a1a]" />
      </div>
      <h3 className="text-base font-bold text-[#161b27]">Something went wrong</h3>
      <p className="mx-auto mt-1 max-w-xs text-sm text-[#544242]">{message}</p>
      <button onClick={onRetry} className="mt-4 rounded-lg bg-[#f08b8b] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#d97a7c]">
        Try again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConnectionsPage — Main Component
// ---------------------------------------------------------------------------

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const { id: routeConnectionId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const openUuid = searchParams.get("openUuid");
  useAuth();

  const { isInAddressBook } = useContactMatch();

  // ---- State ----
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [showNewLinkModal, setShowNewLinkModal] = useState(false);
  const [activeSharingIds, setActiveSharingIds] = useState<Set<number>>(new Set());

  const handleSharingLoaded = useCallback((id: number, active: boolean) => {
    setActiveSharingIds((prev) => {
      if (prev.has(id) === active) return prev;
      const next = new Set(prev);
      if (active) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(() => {
    if (routeConnectionId) {
      const pk = Number(routeConnectionId);
      return Number.isNaN(pk) ? null : pk;
    }
    return null;
  });

  // ---- Fetch connections (all statuses, so the filter is meaningful) ----
  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await listConnections({ size: 200, sort: "connected_customer__first_name" });
      setConnections(all);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError("Unable to connect. Please check your internet connection.");
      } else {
        setError("Failed to load connections. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // ---- Resolve openUuid → modal ----
  useEffect(() => {
    if (!openUuid || loading || connections.length === 0) return;
    const match = connections.find((c) => c.uuid === openUuid);
    if (match) {
      setSelectedConnectionId(match.id);
      navigate("/connections", { replace: true });
    }
  }, [openUuid, loading, connections, navigate]);

  // ---- Auto-grant sweep for new-user registrations ----
  const autoGrantSweepDoneRef = useRef(false);
  useEffect(() => {
    if (autoGrantSweepDoneRef.current || loading || connections.length === 0) return;
    autoGrantSweepDoneRef.current = true;

    const sevenDaysAgo = Date.now() / 1000 - 7 * 24 * 3600;
    const recent = connections.filter((c) => c.created > sevenDaysAgo);
    if (recent.length === 0) return;

    void (async () => {
      for (const conn of recent) {
        try {
          const detail = await getConnection(conn.id);
          const pending = Object.entries(detail.my_sharing ?? {}).filter(([, entry]) => entry?.status === "pending");
          if (pending.length > 0) {
            await Promise.allSettled(
              pending.map(([field, entry]) => grantDataSharing(conn.id, field as SharableField, entry?.mode ?? "show_valid")),
            );
            const updated = await getConnection(conn.id).catch(() => null);
            if (updated) {
              setConnections((prev) => prev.map((c) => (c.id === conn.id ? updated : c)));
            }
          }
        } catch {
          // best-effort
        }
      }
    })();
  }, [loading, connections]);

  // ---- Enrich + dedup ----
  const enrichedConnections = useMemo(() => {
    const enriched = connections.map((c) => enrichConnection(c));
    return deduplicateByName(enriched);
  }, [connections]);

  // ---- Filter by tab + search ----
  const filtered = useMemo(() => {
    let list = enrichedConnections;
    if (statusTab === "verified") list = list.filter((c) => c.status === "verified");
    else if (statusTab === "pending") list = list.filter((c) => c.status === "pending");

    const q = searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter((c) => c.displayName.toLowerCase().includes(q) || (c.phone && c.phone.includes(q)));
    }
    return [...list].sort((a, b) => a.sortFirstName.localeCompare(b.sortFirstName));
  }, [enrichedConnections, statusTab, searchQuery]);

  const handleContactClick = useCallback((connection: EnrichedConnection) => {
    setSelectedConnectionId(connection.id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedConnectionId(null);
    if (routeConnectionId) navigate("/connections", { replace: true });
  }, [routeConnectionId, navigate]);

  const handleLinkCreated = useCallback(() => {
    // New links appear in the Activity screen.
  }, []);

  // ---- Render ----
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 md:py-12">
      {/* Modals */}
      {selectedConnectionId !== null && (
        <ContactDetailModal connectionId={selectedConnectionId} onClose={handleCloseModal} onSharingLoaded={handleSharingLoaded} />
      )}
      <NewTrustLinkModal isOpen={showNewLinkModal} onClose={() => setShowNewLinkModal(false)} onCreated={handleLinkCreated} />

      {/* Hero */}
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-[#161b27] sm:text-3xl">Your Trusted Network</h1>
        <p className="mt-1 text-sm text-[#544242]">Your verified connections — and what you ask for and share with each.</p>
      </div>

      {/* Controls */}
      <Controls search={searchQuery} onSearch={setSearchQuery} tab={statusTab} onTab={setStatusTab} />

      {/* Table card */}
      <div className={`overflow-hidden ${CARD}`}>
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorState message={error} onRetry={fetchConnections} />
        ) : filtered.length === 0 ? (
          <EmptyState tab={statusTab} hasSearch={!!searchQuery.trim()} />
        ) : (
          <>
            <TableHeader />
            <div className="divide-y divide-[#f1f3ff]">
              {filtered.map((conn) => (
                <ContactRow
                  key={conn.id}
                  connection={conn}
                  onClick={handleContactClick}
                  hasActiveSharing={activeSharingIds.has(conn.id)}
                  isSaved={isInAddressBook(conn.phone)}
                />
              ))}
            </div>
            <div className="border-t border-[#f1f3ff] bg-[#faf9ff] px-5 py-3">
              <p className="text-xs text-[#877271]">
                Showing {filtered.length} connection{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>
          </>
        )}
      </div>

      {/* CTA cards */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <button onClick={() => setShowNewLinkModal(true)} className={`flex items-start gap-3 p-5 text-left transition-transform hover:-translate-y-0.5 ${CARD}`}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fef0f0]">
            <Icon name="person_add" className="text-xl text-[#974446]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#161b27]">Add a connection</h3>
            <p className="mt-0.5 text-xs text-[#544242]">Send a Trust Link to invite someone into your verified network.</p>
            <span className="mt-1 inline-block text-xs font-bold text-[#974446]">Begin invitation →</span>
          </div>
        </button>
        <Link to="/activity" className={`flex items-start gap-3 p-5 text-left transition-transform hover:-translate-y-0.5 ${CARD}`}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fef0f0]">
            <Icon name="history" className="text-xl text-[#974446]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#161b27]">Activity ledger</h3>
            <p className="mt-0.5 text-xs text-[#544242]">Review the full history of links, verifications, and connections.</p>
            <span className="mt-1 inline-block text-xs font-bold text-[#974446]">View activity →</span>
          </div>
        </Link>
      </div>

      {/* Mobile FAB */}
      <div className="fixed bottom-20 right-4 z-30 md:hidden">
        <button
          onClick={() => setShowNewLinkModal(true)}
          className="flex items-center gap-2 rounded-full bg-[#f08b8b] px-5 py-3 text-sm font-bold text-white shadow-[0px_8px_32px_rgba(240,139,139,0.35)] transition-all hover:bg-[#d97a7c] active:scale-95"
          aria-label="Create new trust link"
        >
          <Icon name="add" className="text-xl" />
          <span>New Trust Link</span>
        </button>
      </div>
    </div>
  );
}
